<template>
	<div class="slider-wrapper">
		<ul class="slider" ref="sliderRef">
			<li v-for="text in list" class="list-item" ref="itemsRef">
				{{ text }}
			</li>
		</ul>

		<div class="slider-nav">
			<ul>
				<li
					v-for="(_, idx) in list.length"
					:class="{ active: idx === activeIdx }"
					aria-label="slider"
					@click="activeIdx = idx"
				>
					<span />
				</li>
			</ul>
		</div>
	</div>
</template>

<script setup lang="ts">
import { SLIDE_SPEED } from '@/common/constants';
const props = defineProps<{ list: string[] }>();

let interval: NodeJS.Timeout | undefined;
const activeIdx = ref(0);
const itemsRef = useTemplateRef('itemsRef');
const sliderRef = useTemplateRef('sliderRef');

const observer = useIntersectionObserver();

watch(
	activeIdx,
	() => {
		const targetEl = itemsRef.value?.[activeIdx.value];
		if (!targetEl) return;

		targetEl.scrollIntoView({
			block: 'nearest',
			inline: 'nearest',
			behavior: 'smooth'
		});
	},
	{ immediate: true }
);

onMounted(() => {
	if (!sliderRef.value) return;

	observer.createObserver(sliderRef.value, {
		isHidden: {
			handler() {
				clearInterval(interval);
			}
		},
		isIntersecting: {
			handler() {
				runAnimation();
			}
		}
	});
});

function runAnimation() {
	interval = setInterval(() => {
		if (props.list.length === activeIdx.value + 1) {
			activeIdx.value = 0;
			return;
		}

		activeIdx.value += 1;
	}, SLIDE_SPEED);
}
</script>

<style lang="scss" scoped>
.slider-wrapper {
	overflow: hidden;
	position: relative;
}

.slider {
	display: flex;
	overflow: hidden;
	border-radius: var(--border-radius);
	border: 1px solid var(--color-white30);
	width: 100%;
	height: 100%;
}

.list-item {
	@include useFixedSize(100%, 100%);
	@include useFont(txlb);

	white-space: pre-line;
	padding: 0.8889rem;
	text-transform: uppercase;
	color: var(--color-white);
}

.slider-nav {
	position: absolute;
	bottom: 1.333rem;
	left: 1.333rem;
	width: 100%;

	ul {
		position: sticky;
		width: fit-content;
		top: 0;
		display: flex;
		gap: 0.444rem;
		align-items: center;
		flex-wrap: nowrap;

		li {
			display: flex;
			align-items: center;
			justify-content: center;
			border-radius: 1.778rem;
			background-color: transparent;
			border: 0.056rem solid inherit;
			@include useFixedSize(1.111rem, 1.111rem);

			span {
				border-radius: 6px;
				background-color: var(--color-gray300);
				@include useFixedSize(0.444rem, 0.444rem);
			}

			&:hover {
				span {
					background-color: var(--color-white);
				}
			}

			&.active {
				border: 0.056rem solid var(--color-brand500);

				span {
					background-color: var(--color-white);
				}
			}
		}
	}
}

@include screen1024 {
	.list-item {
		padding: 1.333rem;
	}
}
</style>
